/**
 * Configuration API centralisée
 * Remplace la variable globale window.__BASE_API__
 */

import { getAuthHeaders, getAuthToken, notifyUnauthorized, withSseToken } from '../utils/apiAuth';

/**
 * URL de base de l'API en **same-origin** : on appelle toujours l'API sur l'hôte
 * et le port qui servent réellement la page. Le backend sert le build du frontend
 * via StaticFiles (même origine), donc `/api` tombe forcément sur le bon backend,
 * quel que soit le port (8898, 7654, …). Évite « API inaccessible » quand le build
 * a été compilé avec un port différent de celui qui le sert.
 */
const sameOriginApiBase = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return `${window.location.origin}/api`;
  }
  return 'http://localhost:8898/api'; // fallback hors navigateur (tests/SSR)
};

/**
 * Réaligne une URL d'API « localhost » sur l'origine qui sert la page.
 * - URL relative (ex. "/api") → conservée (déjà same-origin).
 * - URL absolue vers localhost/127.0.0.1/0.0.0.0 → réécrite sur l'origine courante
 *   (corrige un port codé en dur dans le build, ex. 7654, servi ailleurs, ex. 8898).
 * - URL absolue vers un vrai domaine → respectée telle quelle (déploiements réels).
 */
const normalizeApiBaseUrl = (url) => {
  try {
    if (typeof window === 'undefined' || !window.location) return url;
    if (!/^https?:\/\//i.test(url)) return url; // relative → same-origin
    const u = new URL(url);
    const isLocal = ['localhost', '127.0.0.1', '0.0.0.0'].includes(u.hostname);
    if (isLocal) {
      return `${window.location.origin}${u.pathname}`.replace(/\/$/, '');
    }
    return url;
  } catch (e) {
    return url;
  }
};

// Configuration par défaut
const DEFAULT_CONFIG = {
  API_BASE_URL: sameOriginApiBase(),
  FRONTEND_PORT: 3898,
  BACKEND_PORT: 8898,
  TIMEOUT: 60000, // 60s (augmenté pour éviter les timeouts)
  // 5 tentatives espacées de 2s, 4s, 6s, 8s faisaient patienter ~20 secondes
  // avant qu'une erreur ne s'affiche. 3 tentatives à 500ms/1s suffisent à
  // absorber une coupure passagère sans figer l'écran.
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 500
};

// Seules les méthodes idempotentes sont rejouées : rejouer un POST, un PUT ou
// un DELETE peut dupliquer l'effet de bord (création, suppression, upload).
const METHODES_IDEMPOTENTES = new Set(['GET', 'HEAD', 'OPTIONS']);

const estRejouable = (options = {}) =>
  METHODES_IDEMPOTENTES.has(String(options.method || 'GET').toUpperCase());

// Configuration depuis variables d'environnement (REACT_APP_API_URL canonique ; REACT_APP_BASE_API = alias rétrocompat)
// Toute URL localhost est réalignée en same-origin → robuste aux mismatch de port build/serveur.
const ENV_CONFIG = {
  API_BASE_URL: normalizeApiBaseUrl(
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_BASE_API ||
    DEFAULT_CONFIG.API_BASE_URL
  ),
  FRONTEND_PORT: 3898, // Port fixe
  BACKEND_PORT: 8898,  // Port fixe
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT) || DEFAULT_CONFIG.TIMEOUT,
  RETRY_ATTEMPTS: parseInt(process.env.REACT_APP_RETRY_ATTEMPTS) || DEFAULT_CONFIG.RETRY_ATTEMPTS,
  RETRY_DELAY: parseInt(process.env.REACT_APP_RETRY_DELAY) || DEFAULT_CONFIG.RETRY_DELAY
};

/**
 * Configuration API finale
 */
export const API_CONFIG = {
  ...ENV_CONFIG,
  
  // URLs dérivées
  get HEALTH_URL() {
    return `${this.API_BASE_URL}/health`;
  },
  
  get DOCS_URL() {
    return this.API_BASE_URL.replace('/api', '/docs');
  },
  
  get SSE_URL() {
    return `${this.API_BASE_URL}/events/stream`;
  },

  // Endpoints spécifiques
  get BOOKS_URL() {
    return `${this.API_BASE_URL}/books`;
  },

  get CONVERSATIONS_URL() {
    return `${this.API_BASE_URL}/conversations`;
  },

  get IMPORT_URL() {
    return `${this.API_BASE_URL}/import`;
  },

  get EXPORT_URL() {
    return `${this.API_BASE_URL}/export`;
  },

  get BACKUP_URL() {
    return `${this.API_BASE_URL}/backup`;
  },

  get CACHE_URL() {
    return `${this.API_BASE_URL}/cache`;
  },

  get KOREADER_URL() {
    return `${this.API_BASE_URL}/koreader`;
  }
};

/** Base URL API (ex. http://localhost:8898/api), sans slash final inutile. */
export function getApiBaseUrl() {
  return API_CONFIG.API_BASE_URL.replace(/\/$/, '');
}

/**
 * Construire URL API de façon sécurisée
 * @param {string} endpoint - Endpoint (sans préfixe /api)
 * @returns {string} URL complète
 */
export const buildApiUrl = (endpoint = '') => {
  // Nettoyer l'endpoint
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  
  // Construire URL
  return cleanEndpoint ? 
    `${API_CONFIG.API_BASE_URL.replace(/\/$/, '')}/${cleanEndpoint}` :
    API_CONFIG.API_BASE_URL.replace(/\/$/, '');
};

// Fonction pour mettre à jour les statistiques API
function updateApiStats(responseTime, isError = false) {
  try {
    const stats = JSON.parse(localStorage.getItem('apiStats') || '{}');
    stats.requests = (stats.requests || 0) + 1;
    if (isError) {
      stats.errors = (stats.errors || 0) + 1;
    }
    
    // Calculer le temps de réponse moyen
    const totalTime = (stats.totalResponseTime || 0) + responseTime;
    stats.totalResponseTime = totalTime;
    stats.avgResponseTime = totalTime / stats.requests;
    
    localStorage.setItem('apiStats', JSON.stringify(stats));
  } catch (error) {
    console.error('Erreur mise à jour stats API:', error);
  }
}

/**
 * Gestionnaire d'erreurs standardisé
 */
export class ApiError extends Error {
  constructor(message, status, endpoint) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.endpoint = endpoint;
    this.timestamp = new Date();
  }
}

/**
 * Fetch avec configuration centralisée et gestion d'erreurs
 * @param {string} endpoint - Endpoint API
 * @param {RequestInit} options - Options fetch
 * @returns {Promise<Response>} Réponse fetch
 */
export const apiFetch = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const startTime = performance.now();

  // `signal` de l'appelant : il était écrasé par le contrôleur de délai interne,
  // si bien qu'un composant démonté ne pouvait pas annuler son téléchargement.
  const { headers: userHeaders, signal: signalAppelant, ...restOptions } = options;
  const mergedHeaders =
    options.body instanceof FormData
      ? { ...getAuthHeaders(), ...(userHeaders || {}) }
      : {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
          ...(userHeaders || {}),
        };

  const defaultOptions = {
    timeout: API_CONFIG.TIMEOUT,
    headers: mergedHeaders,
  };

  const finalOptions = { ...defaultOptions, ...restOptions, headers: mergedHeaders };
  
  // Retry logic
  let lastError;
  const rejouable = estRejouable(finalOptions);
  const tentatives = rejouable ? API_CONFIG.RETRY_ATTEMPTS : 1;

  for (let attempt = 1; attempt <= tentatives; attempt++) {
    // Annulation voulue par l'appelant : on la propage telle quelle, sans
    // nouvelle tentative ni transformation en ApiError.
    if (signalAppelant?.aborted) {
      throw new DOMException('Requête annulée', 'AbortError');
    }
    const controller = new AbortController();
    const relaisAnnulation = () => controller.abort();
    // Le relais reste branché après la réponse : la lecture du corps (blob,
    // arrayBuffer) dépend de ce même contrôleur et doit rester annulable.
    signalAppelant?.addEventListener?.('abort', relaisAnnulation, { once: true });
    try {
      const timeoutId = setTimeout(() => controller.abort(), finalOptions.timeout);

      let response;
      try {
        response = await fetch(url, {
          ...finalOptions,
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }

      // Mettre à jour les statistiques
      const responseTime = performance.now() - startTime;
      updateApiStats(responseTime, !response.ok);

      // Une erreur temporaire (5xx) mérite un nouvel essai, mais uniquement
      // pour une méthode idempotente.
      if (response.status >= 500 && attempt < tentatives) {
        lastError = new ApiError(`HTTP ${response.status} (tentative ${attempt}/${tentatives})`, response.status, endpoint);
        signalAppelant?.removeEventListener?.('abort', relaisAnnulation);
        await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY * attempt));
        continue;
      }

      // Dès qu'une réponse existe, on la renvoie — y compris en 4xx/5xx. Lever
      // ici privait les appelants du contrat `fetch` (`if (!response.ok)`) et
      // du corps de la réponse, donc du détail de l'erreur serveur.
      return response;

    } catch (error) {
      signalAppelant?.removeEventListener?.('abort', relaisAnnulation);
      if (signalAppelant?.aborted) throw error;
      lastError = error;

      // Gestion spéciale pour les erreurs de signal aborté
      if (error.name === 'AbortError' || error.message.includes('aborted')) {
        console.warn(`apiFetch tentative ${attempt} annulée (timeout):`, { endpoint, url });
      } else {
        // Log pour debug des autres erreurs
        console.warn(`apiFetch tentative ${attempt} échouée:`, { endpoint, url, error: error.message });
      }

      if (attempt < tentatives) {
        await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY * attempt));
        continue;
      }
    }
  }
  
  // Si toutes les tentatives ont échoué, créer une ApiError appropriée
  if (lastError instanceof ApiError) {
    throw lastError;
  }
  
  // S'assurer que lastError est un objet valide
  if (!lastError || typeof lastError !== 'object') {
    const errorMessage = typeof lastError === 'string' ? lastError : 'Erreur réseau inconnue';
    throw new ApiError(errorMessage, 0, endpoint);
  }
  
  // Créer une ApiError avec les informations disponibles
  const errorMessage = lastError?.message || 'Erreur réseau inconnue';
  const status = lastError?.status || 0;
  throw new ApiError(errorMessage, status, endpoint);
};

/**
 * Fetch JSON avec gestion d'erreurs standardisée
 * @param {string} endpoint - Endpoint API
 * @param {RequestInit} options - Options fetch
 * @returns {Promise<any>} Données JSON
 */
export const apiJSON = async (endpoint, options = {}) => {
  try {
    const response = await apiFetch(endpoint, options);
    
    if (!response.ok) {
      if (response.status === 401) notifyUnauthorized();
      let errorMessage = `HTTP ${response.status}`;

      try {
        const errorData = await response.json();
        // Contrat d'erreur unifié (audit P2-13) : `error.message` d'abord.
        // `detail` reste lu pour les réponses antérieures au contrat, et
        // `error` n'est utilisé que s'il s'agit encore d'une chaîne — sans
        // cela un objet s'afficherait « [object Object] ».
        errorMessage =
          errorData?.error?.message ||
          errorData?.detail ||
          (typeof errorData?.error === 'string' ? errorData.error : null) ||
          errorMessage;
      } catch {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      throw new ApiError(errorMessage, response.status, endpoint);
    }
    
    const data = await response.json();

    // Garde-fou du contrat : un corps qui se déclare en échec ne doit jamais
    // être présenté comme un résultat, même si le statut HTTP est 200. Sans
    // cela, une erreur applicative se lit comme une liste vide.
    if (data && data.success === false) {
      throw new ApiError(
        data?.error?.message || data?.detail || 'Erreur applicative',
        response.status,
        endpoint
      );
    }

    return data;
  } catch (error) {
    // Log pour debug
    console.warn('apiJSON error:', { endpoint, error, errorType: typeof error });
    
    // Si c'est déjà une ApiError, la relancer
    if (error instanceof ApiError) {
      throw error;
    }
    
    // S'assurer que l'erreur est toujours un objet valide
    if (!error || typeof error !== 'object') {
      const errorMessage = typeof error === 'string' ? error : 'Erreur réseau inconnue';
      throw new ApiError(errorMessage, 0, endpoint);
    }
    
    // Sinon, créer une ApiError avec les informations disponibles
    const errorMessage = error.message || 'Erreur réseau inconnue';
    const status = error.status || 0;
    throw new ApiError(errorMessage, status, endpoint);
  }
};

/**
 * Upload de fichier avec progress et gestion d'erreurs
 * @param {string} endpoint - Endpoint upload
 * @param {FormData} formData - Données à uploader
 * @param {function} onProgress - Callback progress (optionnel)
 * @returns {Promise<any>} Résultat upload
 */
export const apiUpload = async (endpoint, formData, onProgress = null) => {
  const url = buildApiUrl(endpoint);
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });
    }
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText);
          resolve(result);
        } catch (error) {
          resolve({ ok: true }); // Réponse non-JSON considérée comme succès
        }
      } else {
        // Même traitement du 401 que `apiJSON` : sans cela, une session expirée
        // pendant un import laissait l'utilisateur sur un échec inexpliqué au
        // lieu de le renvoyer vers la page de connexion.
        if (xhr.status === 401) notifyUnauthorized();
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new ApiError(error.detail || error.error || `HTTP ${xhr.status}`, xhr.status, endpoint));
        } catch {
          reject(new ApiError(`HTTP ${xhr.status}: ${xhr.statusText}`, xhr.status, endpoint));
        }
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new ApiError('Erreur réseau upload', 0, endpoint));
    });
    
    xhr.addEventListener('abort', () => {
      reject(new ApiError('Upload annulé', 0, endpoint));
    });
    
    xhr.open('POST', url);

    // `XMLHttpRequest` ne passe pas par `apiFetch` : les en-têtes d'authentification
    // n'étaient donc jamais posés. Tout import échouait en 401 dès que l'auth était
    // activée, alors que le reste de l'application fonctionnait.
    // On ne touche PAS à `Content-Type` : le navigateur doit générer lui-même la
    // frontière (boundary) du multipart.
    const authHeaders = getAuthHeaders();
    Object.entries(authHeaders).forEach(([nom, valeur]) => {
      if (valeur) xhr.setRequestHeader(nom, valeur);
    });

    xhr.send(formData);
  });
};

/** Nombre d'echecs CONSECUTIFS au-dela duquel on cesse de se reconnecter. */
export const MAX_TENTATIVES_SSE = 5;
/** Delai de base entre deux tentatives ; il double a chaque echec. */
export const DELAI_SSE_MS = 3000;

/**
 * Gestionnaire SSE avec reconnexion automatique
 * @param {string} endpoint - Endpoint SSE
 * @param {function} onMessage - Callback message
 * @param {function} onError - Callback erreur
 * @returns {EventSource} Instance EventSource
 */
export const createSSEConnection = (endpoint, onMessage, onError = null) => {
  // La reconnexion créait une NOUVELLE EventSource sans la rendre à l'appelant :
  // celui-ci conservait la référence fermée, si bien que son `close()` de
  // nettoyage ne fermait plus le flux réellement actif — une connexion fuyait à
  // chaque reconnexion. On renvoie donc une poignée stable, dont `close()` ferme
  // toujours la connexion courante et interrompt les reconnexions.
  let connexionCourante = null;
  let fermeParAppelant = false;
  let echecs = 0;

  const brancher = () => {
    if (fermeParAppelant) return;

    const url = withSseToken(buildApiUrl(endpoint));
    connexionCourante = new EventSource(url);

    connexionCourante.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (error) {
        console.warn('Erreur parsing SSE:', error);
        if (onError) onError(error);
      }
    };

    connexionCourante.onerror = (error) => {
      console.warn('SSE Error:', error);

      // [2026-09-09] La reconnexion etait INCONDITIONNELLE et SANS FIN. Mesure
      // en production sur `biblio-rag` : 6486 requetes en 24 h sur
      // `/api/tasks/sse/events/tasks`, TOUTES en 401, ~22 par minute, un seul
      // jeton en cause. Un onglet laisse ouvert avec une session expiree
      // martelait la route indefiniment. Trois consequences : du bruit qui
      // noie les vraies erreurs dans les journaux, le jeton reecrit en clair
      // dans les traces a chaque tentative (il voyage en query string, faute
      // d'en-tetes sur EventSource), et de la charge inutile.
      //
      // On coupe donc la boucle sur les deux cas ou reessayer est vain :
      //   - aucun jeton en reserve : se reconnecter ne peut pas reussir ;
      //   - trop d'echecs consecutifs : la session est probablement expiree,
      //     on previent l'application via `notifyUnauthorized()` — l'écran de
      //     connexion prend le relais — et on cesse.
      // Entre les deux, le delai croit (3 s, 6 s, 12 s...) au lieu de rester
      // fixe : une coupure reseau reelle est toujours rattrapee, sans marteler.
      if (connexionCourante?.readyState === EventSource.CLOSED && !fermeParAppelant) {
        if (!getAuthToken()) {
          console.warn('SSE : aucun jeton, reconnexion abandonnee.');
        } else if (echecs >= MAX_TENTATIVES_SSE) {
          console.warn(`SSE : ${echecs} echecs consecutifs, session probablement expiree.`);
          notifyUnauthorized();
        } else {
          const delai = DELAI_SSE_MS * 2 ** echecs;
          echecs += 1;
          console.log(`SSE ferme, reconnexion dans ${delai} ms (tentative ${echecs}/${MAX_TENTATIVES_SSE}).`);
          setTimeout(brancher, delai);
        }
      }

      if (onError) onError(error);
    };

    // Une connexion qui s'ouvre remet le compteur a zero : seuls les echecs
    // CONSECUTIFS doivent conduire a l'abandon.
    connexionCourante.onopen = () => {
      echecs = 0;
    };
  };

  brancher();

  return {
    close() {
      fermeParAppelant = true;
      connexionCourante?.close?.();
    },
    get readyState() {
      return connexionCourante ? connexionCourante.readyState : EventSource.CLOSED;
    },
  };
};

/**
 * Vérifier santé API
 * @returns {Promise<boolean>} API disponible
 */
export const checkApiHealth = async () => {
  try {
    const response = await apiFetch('health');
    return response.ok;
  } catch (error) {
    console.warn('API santé échouée:', error.message);
    return false;
  }
};

/**
 * Vérifier connectivité API avec détails
 * @returns {Promise<Object>} Statut détaillé de l'API
 */
export const checkApiConnectivity = async () => {
  const startTime = performance.now();
  
  try {
    const response = await fetch(buildApiUrl('health'), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: AbortSignal.timeout(5000) // 5s timeout
    });
    
    const responseTime = performance.now() - startTime;
    
    if (response.ok) {
      const data = await response.json();
      return {
        status: 'connected',
        responseTime: Math.round(responseTime),
        data: data
      };
    } else {
      return {
        status: 'error',
        statusCode: response.status,
        statusText: response.statusText,
        responseTime: Math.round(responseTime)
      };
    }
  } catch (error) {
    const responseTime = performance.now() - startTime;
    return {
      status: 'unreachable',
      error: error.message,
      responseTime: Math.round(responseTime),
      url: buildApiUrl('health')
    };
  }
};

/**
 * Initialiser configuration API côté client
 */
export const initApiConfig = () => {
  // Rétrocompatibilité avec window.__BASE_API__
  if (!window.__BASE_API__) {
    window.__BASE_API__ = API_CONFIG.API_BASE_URL;
  }
  
  console.log('🔧 Configuration API initialisée:', {
    base: API_CONFIG.API_BASE_URL,
    frontend: API_CONFIG.FRONTEND_PORT,
    backend: API_CONFIG.BACKEND_PORT
  });
  
  return API_CONFIG;
};

// Export par défaut
export default API_CONFIG;
